import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CircularLoading from '../components/CircularLoading';
import { api } from 'common';
import { makeStyles } from '@mui/styles';
import {
  Typography, Grid, Chip, Button as MuiButton, TextField, Paper, Avatar
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PublishIcon from '@mui/icons-material/Publish';
import CalculateIcon from '@mui/icons-material/Calculate';
import ArchiveIcon from '@mui/icons-material/Archive';
import { SECONDORY_COLOR, MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';
import { useTranslation } from 'react-i18next';
import moment from 'moment/min/moment-with-locales';

const useStyles = makeStyles(() => ({
  root: { padding: '24px' },
  card: {
    padding: '20px',
    borderRadius: '8px',
    boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
    marginBottom: '20px',
  },
  sectionTitle: { fontFamily: FONT_FAMILY, marginBottom: '16px !important' },
  chip: { margin: '4px !important', fontFamily: FONT_FAMILY },
  winnerCard: {
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${SECONDORY_COLOR}`,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  winnerName: { fontFamily: FONT_FAMILY, fontWeight: 'bold' },
  winnerSub: { fontFamily: FONT_FAMILY, color: '#666', fontSize: '13px' },
  actionBtn: { marginRight: '8px !important', marginBottom: '8px !important' },
}));

const DRIVER_CATEGORIES = [
  { key: 'bestFuelSaver',    label: 'Best Fuel Saver' },
  { key: 'bestRated',        label: 'Best Rated Driver' },
  { key: 'mostRides',        label: 'Most Completed Rides' },
  { key: 'ambassador',       label: 'Ambassador of the Week' },
];

function weekId(date) {
  const d = date || new Date();
  const start = new Date(d);
  start.setDate(d.getDate() - d.getDay());
  return `week_${start.getFullYear()}_${String(start.getMonth() + 1).padStart(2, '0')}_${String(start.getDate()).padStart(2, '0')}`;
}

function weekRange(date) {
  const d = date || new Date();
  const start = new Date(d);
  start.setDate(d.getDate() - d.getDay());
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}

export default function WeeklyRecognition() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const isRTL = i18n.dir();

  const { fetchWeeklyRecognition, saveWeeklyRecognition, fetchSawiSpecial, saveSawiSpecialCustomer, saveSawiSpecialDriver } = api;
  const weeklyData = useSelector(s => s.weeklyRecognitionData);
  const sawiSpecialData = useSelector(s => s.sawiSpecialData);
  const bookings = useSelector(s => s.bookinglistdata.bookings);
  const usersdata = useSelector(s => s.usersdata.users);
  const auth = useSelector(s => s.auth);

  const [currentWeekId] = useState(weekId());
  const [draft, setDraft] = useState(null);
  const [computing, setComputing] = useState(false);

  const [showSpecial, setShowSpecial] = useState(false);
  const [specialCustomerSearch, setSpecialCustomerSearch] = useState('');
  const [specialDriverSearch, setSpecialDriverSearch] = useState('');
  const [selectedSpecialCustomer, setSelectedSpecialCustomer] = useState(null);
  const [selectedSpecialDriver, setSelectedSpecialDriver] = useState(null);

  const [ambassadorSearch, setAmbassadorSearch] = useState('');
  const [selectedAmbassador, setSelectedAmbassador] = useState(null);

  useEffect(() => {
    dispatch(fetchWeeklyRecognition());
    dispatch(fetchSawiSpecial());
  }, [dispatch, fetchWeeklyRecognition, fetchSawiSpecial]);

  const handlePublishSpecialCustomer = () => {
    if (!selectedSpecialCustomer) return;
    dispatch(saveSawiSpecialCustomer({
      uid: selectedSpecialCustomer.id,
      name: `${selectedSpecialCustomer.firstName} ${selectedSpecialCustomer.lastName}`,
      avatar: selectedSpecialCustomer.profile_image || null,
      active: true,
    }));
  };

  const handleHideSpecialCustomer = () => {
    const current = sawiSpecialData?.data?.customer;
    if (!current) return;
    dispatch(saveSawiSpecialCustomer({ ...current, active: false }));
  };

  const handlePublishSpecialDriver = () => {
    if (!selectedSpecialDriver) return;
    dispatch(saveSawiSpecialDriver({
      uid: selectedSpecialDriver.id,
      name: `${selectedSpecialDriver.firstName} ${selectedSpecialDriver.lastName}`,
      avatar: selectedSpecialDriver.profile_image || null,
      active: true,
    }));
  };

  const handleHideSpecialDriver = () => {
    const current = sawiSpecialData?.data?.driver;
    if (!current) return;
    dispatch(saveSawiSpecialDriver({ ...current, active: false }));
  };

  const handleAssignAmbassador = () => {
    if (!selectedAmbassador || !draft) return;
    const ambassadorData = {
      uid: selectedAmbassador.id,
      name: `${selectedAmbassador.firstName} ${selectedAmbassador.lastName}`,
      avatar: selectedAmbassador.profile_image || null,
    };
    const updatedWinners = { ...(draft.driverWinners || {}), ambassador: ambassadorData };
    const updated = { ...draft, driverWinners: updatedWinners };
    delete updated.id;
    setDraft(prev => ({ ...prev, driverWinners: updatedWinners }));
    dispatch(saveWeeklyRecognition(currentWeekId, updated));
    setAmbassadorSearch('');
    setSelectedAmbassador(null);
  };

  const handleClearAmbassador = () => {
    if (!draft) return;
    const updatedWinners = { ...(draft.driverWinners || {}), ambassador: null };
    const updated = { ...draft, driverWinners: updatedWinners };
    delete updated.id;
    setDraft(prev => ({ ...prev, driverWinners: updatedWinners }));
    dispatch(saveWeeklyRecognition(currentWeekId, updated));
  };

  useEffect(() => {
    if (weeklyData.data) {
      const existing = weeklyData.data.find(e => e.id === currentWeekId);
      if (existing) setDraft(existing);
    }
  }, [weeklyData.data, currentWeekId]);

  const handleCalculate = () => {
    if (!bookings || !usersdata) { alert('Loading data, please wait...'); return; }
    setComputing(true);

    const { start, end } = weekRange();
    const weekBookings = bookings.filter(b => {
      if (b.status !== 'PAID' && b.status !== 'COMPLETE') return false;
      const d = new Date(b.tripdate || b.bookingDate);
      return d >= start && d <= end;
    });

    // Calculate per-driver stats
    const driverStats = {};
    weekBookings.forEach(b => {
      if (!b.driver) return;
      if (!driverStats[b.driver]) driverStats[b.driver] = { rides: 0, totalFare: 0, totalRating: 0, ratingCount: 0 };
      driverStats[b.driver].rides++;
      driverStats[b.driver].totalFare += parseFloat(b.trip_cost || b.estimate || 0);
      if (b.driverStars) { driverStats[b.driver].totalRating += b.driverStars; driverStats[b.driver].ratingCount++; }
    });

    const getDriver = (uid) => usersdata.find(u => u.id === uid) || { firstName: 'Driver', lastName: uid?.slice(0, 6) };

    const winners = {};
    const driversWithRides = Object.entries(driverStats).filter(([, s]) => s.rides > 0);

    // Most rides
    const mostRides = driversWithRides.sort((a, b) => b[1].rides - a[1].rides)[0];
    if (mostRides) {
      const d = getDriver(mostRides[0]);
      winners.mostRides = { uid: mostRides[0], name: `${d.firstName} ${d.lastName}`, rides: mostRides[1].rides, avatar: d.profile_image || null };
    }

    // Best rated
    const bestRated = driversWithRides.filter(([, s]) => s.ratingCount > 0)
      .sort((a, b) => (b[1].totalRating / b[1].ratingCount) - (a[1].totalRating / a[1].ratingCount))[0];
    if (bestRated) {
      const d = getDriver(bestRated[0]);
      const avg = (bestRated[1].totalRating / bestRated[1].ratingCount).toFixed(1);
      winners.bestRated = { uid: bestRated[0], name: `${d.firstName} ${d.lastName}`, rating: avg, avatar: d.profile_image || null };
    }

    // Best fuel saver (highest net ratio — approximated by most rides + highest earnings)
    const bestFuel = driversWithRides.sort((a, b) => b[1].totalFare - a[1].totalFare)[0];
    if (bestFuel) {
      const d = getDriver(bestFuel[0]);
      winners.bestFuelSaver = { uid: bestFuel[0], name: `${d.firstName} ${d.lastName}`, fare: bestFuel[1].totalFare, avatar: d.profile_image || null };
    }

    // Customer stats
    const customerStats = {};
    weekBookings.forEach(b => {
      if (!b.customer) return;
      if (!customerStats[b.customer]) customerStats[b.customer] = { rides: 0 };
      customerStats[b.customer].rides++;
    });
    const topCustomer = Object.entries(customerStats).sort((a, b) => b[1].rides - a[1].rides)[0];
    let bestCustomer = null;
    if (topCustomer && topCustomer[1].rides >= 3) {
      const c = getDriver(topCustomer[0]);
      bestCustomer = { uid: topCustomer[0], name: `${c.firstName} ${c.lastName}`, rides: topCustomer[1].rides, avatar: c.profile_image || null };
    }

    const newDraft = {
      weekStart: start.toISOString(),
      weekEnd: end.toISOString(),
      status: 'draft',
      calculatedAt: Date.now(),
      driverWinners: winners,
      bestCustomer,
    };

    setDraft({ id: currentWeekId, ...newDraft });
    dispatch(saveWeeklyRecognition(currentWeekId, newDraft));
    setComputing(false);
  };

  const handlePublish = () => {
    if (!draft) return;
    // Archive any other currently published weeks so only one is ever active
    if (weeklyData.data) {
      weeklyData.data
        .filter(e => e.id !== currentWeekId && e.status === 'published')
        .forEach(e => {
          const archived = { ...e, status: 'archived' };
          delete archived.id;
          dispatch(saveWeeklyRecognition(e.id, archived));
        });
    }
    const updated = { ...draft, status: 'published', publishedAt: Date.now(), publishedBy: auth.profile.uid };
    delete updated.id;
    setDraft({ ...draft, status: 'published', publishedAt: updated.publishedAt });
    dispatch(saveWeeklyRecognition(currentWeekId, updated));
  };

  const handleUnpublish = () => {
    if (!draft) return;
    const updated = { ...draft, status: 'draft' };
    delete updated.id;
    setDraft({ ...draft, status: 'draft' });
    dispatch(saveWeeklyRecognition(currentWeekId, updated));
  };

  const handleArchive = () => {
    if (!draft) return;
    const updated = { ...draft, status: 'archived' };
    delete updated.id;
    setDraft({ ...draft, status: 'archived' });
    dispatch(saveWeeklyRecognition(currentWeekId, updated));
  };

  const statusColor = { draft: '#f0a500', published: '#2e7d32', archived: '#888' };

  return (
    weeklyData.loading && !draft ? <CircularLoading /> :
      <div className={classes.root} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
        <Typography variant="h5" className={classes.sectionTitle} style={{ fontFamily: FONT_FAMILY }}>
          <EmojiEventsIcon style={{ verticalAlign: 'middle', marginRight: 8, color: '#f0a500' }} />
          Weekly Recognition
        </Typography>

        {/* Current week info */}
        <Paper className={classes.card}>
          <Typography style={{ fontFamily: FONT_FAMILY, marginBottom: '8px' }}>
            <strong>Current Week:</strong> {moment(weekRange().start).format('MMM D')} – {moment(weekRange().end).format('MMM D, YYYY')}
          </Typography>
          {draft && (
            <Chip
              label={`Status: ${draft.status?.toUpperCase()}`}
              style={{ backgroundColor: statusColor[draft.status] || '#888', color: '#fff', fontFamily: FONT_FAMILY }}
            />
          )}
          {draft?.calculatedAt && (
            <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '13px', color: '#666', marginTop: '6px' }}>
              Last calculated: {moment(draft.calculatedAt).format('lll')}
            </Typography>
          )}

          <div style={{ marginTop: '16px' }}>
            <MuiButton
              variant="contained"
              startIcon={<CalculateIcon />}
              onClick={handleCalculate}
              disabled={computing}
              className={classes.actionBtn}
              style={{ backgroundColor: MAIN_COLOR, color: '#fff', marginRight: '8px', marginBottom: '8px' }}
            >
              {computing ? 'Calculating...' : draft ? 'Recalculate' : 'Calculate Winners'}
            </MuiButton>

            {draft && draft.status === 'draft' && (
              <MuiButton
                variant="contained"
                startIcon={<PublishIcon />}
                onClick={handlePublish}
                className={classes.actionBtn}
                style={{ backgroundColor: '#2e7d32', color: '#fff', marginRight: '8px', marginBottom: '8px' }}
              >
                Publish
              </MuiButton>
            )}
            {draft && draft.status === 'published' && (
              <MuiButton
                variant="outlined"
                onClick={handleUnpublish}
                className={classes.actionBtn}
                style={{ marginRight: '8px', marginBottom: '8px' }}
              >
                Unpublish
              </MuiButton>
            )}
            {draft && draft.status !== 'archived' && (
              <MuiButton
                variant="outlined"
                startIcon={<ArchiveIcon />}
                onClick={handleArchive}
                className={classes.actionBtn}
                style={{ marginBottom: '8px' }}
              >
                Archive
              </MuiButton>
            )}
          </div>
        </Paper>

        {/* Ambassador of the Week — manual picker */}
        {draft && (
          <Paper className={classes.card}>
            <Typography variant="h6" className={classes.sectionTitle}>
              🤝 Ambassador of the Week
            </Typography>

            {/* Current ambassador */}
            {draft.driverWinners?.ambassador ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <Avatar src={draft.driverWinners.ambassador.avatar || undefined} style={{ backgroundColor: '#f0a500' }}>
                  {draft.driverWinners.ambassador.name?.[0]}
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>{draft.driverWinners.ambassador.name}</Typography>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '12px', color: '#666' }}>Assigned as Ambassador</Typography>
                </div>
                <MuiButton size="small" variant="outlined" color="error" onClick={handleClearAmbassador} style={{ fontFamily: FONT_FAMILY }}>
                  Clear
                </MuiButton>
              </div>
            ) : (
              <Typography style={{ fontFamily: FONT_FAMILY, color: '#999', fontSize: '13px', fontStyle: 'italic', marginBottom: '12px' }}>
                No ambassador assigned for this week
              </Typography>
            )}

            <TextField
              fullWidth
              size="small"
              label="Search driver by name or phone"
              value={ambassadorSearch}
              onChange={e => { setAmbassadorSearch(e.target.value); setSelectedAmbassador(null); }}
              style={{ marginBottom: '8px' }}
            />
            {ambassadorSearch.length > 0 && (
              <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '10px' }}>
                {(usersdata || [])
                  .filter(u => {
                    if (u.usertype !== 'driver') return false;
                    const q = ambassadorSearch.toLowerCase();
                    return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
                      (u.mobile || '').includes(ambassadorSearch);
                  })
                  .slice(0, 10)
                  .map(u => (
                    <div
                      key={u.id}
                      onClick={() => { setSelectedAmbassador(u); setAmbassadorSearch(`${u.firstName} ${u.lastName}`); }}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                        backgroundColor: selectedAmbassador?.id === u.id ? '#e8f5e9' : 'transparent',
                      }}
                    >
                      <Avatar src={u.profile_image || undefined} style={{ width: 30, height: 30, fontSize: 13 }}>
                        {u.firstName?.[0]}
                      </Avatar>
                      <div>
                        <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '13px' }}>{u.firstName} {u.lastName}</Typography>
                        {u.mobile ? <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '11px', color: '#888' }}>{u.mobile}</Typography> : null}
                      </div>
                    </div>
                  ))
                }
              </div>
            )}
            <MuiButton
              variant="contained"
              disabled={!selectedAmbassador}
              onClick={handleAssignAmbassador}
              style={{ backgroundColor: MAIN_COLOR, color: '#fff', fontFamily: FONT_FAMILY }}
            >
              Assign Ambassador
            </MuiButton>
          </Paper>
        )}

        {draft?.driverWinners && (
          <Paper className={classes.card}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Driver Winners
            </Typography>
            <Grid container spacing={2}>
              {DRIVER_CATEGORIES.map(cat => {
                const winner = draft.driverWinners[cat.key];
                return (
                  <Grid item xs={12} sm={6} key={cat.key}>
                    <div className={classes.winnerCard}>
                      <Avatar src={winner?.avatar || undefined} style={{ backgroundColor: '#f0a500' }}>
                        {winner ? winner.name?.[0] : '?'}
                      </Avatar>
                      <div>
                        <Typography className={classes.winnerName}>{cat.label}</Typography>
                        {winner ? (
                          <Typography className={classes.winnerSub}>
                            {winner.name}
                            {winner.rides ? ` — ${winner.rides} rides` : ''}
                            {winner.rating ? ` — ${winner.rating}★` : ''}
                          </Typography>
                        ) : (
                          <Typography className={classes.winnerSub} style={{ fontStyle: 'italic' }}>
                            Not enough data
                          </Typography>
                        )}
                      </div>
                    </div>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}

        {draft?.calculatedAt && (
          <Paper className={classes.card}>
            <Typography variant="h6" className={classes.sectionTitle}>Best Customer of the Week</Typography>
            {draft.bestCustomer ? (
              <div className={classes.winnerCard}>
                <Avatar src={draft.bestCustomer.avatar || undefined} style={{ backgroundColor: MAIN_COLOR }}>
                  {draft.bestCustomer.name?.[0] || 'C'}
                </Avatar>
                <div>
                  <Typography className={classes.winnerName}>{draft.bestCustomer.name}</Typography>
                  <Typography className={classes.winnerSub}>{draft.bestCustomer.rides} completed rides this week</Typography>
                </div>
              </div>
            ) : (
              <Typography style={{ fontFamily: FONT_FAMILY, color: '#666', fontStyle: 'italic' }}>
                Not enough data this week (minimum 3 rides required)
              </Typography>
            )}
          </Paper>
        )}

        {/* Past weeks (archived) */}
        {weeklyData.data && weeklyData.data.filter(e => e.id !== currentWeekId && e.status === 'archived').length > 0 && (
          <Paper className={classes.card}>
            <Typography variant="h6" className={classes.sectionTitle}>Past Weeks</Typography>
            {weeklyData.data
              .filter(e => e.id !== currentWeekId && e.status === 'archived')
              .sort((a, b) => (b.publishedAt || 0) - (a.publishedAt || 0))
              .slice(0, 5)
              .map(entry => (
                <div key={entry.id} style={{ marginBottom: '8px', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '14px' }}>
                    <strong>{moment(entry.weekStart).format('MMM D')} – {moment(entry.weekEnd).format('MMM D, YYYY')}</strong>
                    {entry.driverWinners?.mostRides && ` — Most rides: ${entry.driverWinners.mostRides.name}`}
                    {entry.driverWinners?.ambassador && ` — Ambassador: ${entry.driverWinners.ambassador.name}`}
                  </Typography>
                </div>
              ))
            }
          </Paper>
        )}

        {/* Sawi Special Assignment */}
        <Paper className={classes.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <Typography variant="h6" className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              🌟 Sawi Special Assignment
            </Typography>
            <MuiButton variant="outlined" onClick={() => setShowSpecial(!showSpecial)} style={{ fontFamily: FONT_FAMILY }}>
              {showSpecial ? 'Close' : 'Assign Sawi Special'}
            </MuiButton>
          </div>

          {/* Current status summary */}
          <div style={{ marginTop: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {sawiSpecialData?.data?.customer ? (
              <Chip
                avatar={<Avatar src={sawiSpecialData.data.customer.avatar || undefined}>{sawiSpecialData.data.customer.name?.[0]}</Avatar>}
                label={`Special Customer: ${sawiSpecialData.data.customer.name} — ${sawiSpecialData.data.customer.active ? '✅ VISIBLE' : '🔴 HIDDEN'}`}
                style={{ fontFamily: FONT_FAMILY, backgroundColor: sawiSpecialData.data.customer.active ? '#e8f5e9' : '#fce4ec' }}
              />
            ) : <Typography style={{ fontFamily: FONT_FAMILY, color: '#999', fontSize: '13px' }}>No special customer assigned</Typography>}
            {sawiSpecialData?.data?.driver ? (
              <Chip
                avatar={<Avatar src={sawiSpecialData.data.driver.avatar || undefined}>{sawiSpecialData.data.driver.name?.[0]}</Avatar>}
                label={`Special Driver: ${sawiSpecialData.data.driver.name} — ${sawiSpecialData.data.driver.active ? '✅ VISIBLE' : '🔴 HIDDEN'}`}
                style={{ fontFamily: FONT_FAMILY, backgroundColor: sawiSpecialData.data.driver.active ? '#e8f5e9' : '#fce4ec' }}
              />
            ) : <Typography style={{ fontFamily: FONT_FAMILY, color: '#999', fontSize: '13px' }}>No special driver assigned</Typography>}
          </div>

          {showSpecial && (
            <Grid container spacing={3} style={{ marginTop: '16px' }}>
              {/* Special Customer Picker */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', marginBottom: '8px' }}>
                  👑 Sawi Special Customer
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by name or phone"
                  value={specialCustomerSearch}
                  onChange={e => { setSpecialCustomerSearch(e.target.value); setSelectedSpecialCustomer(null); }}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '10px' }}>
                  {(usersdata || [])
                    .filter(u => {
                      if (u.usertype !== 'customer' || !specialCustomerSearch.length) return false;
                      const q = specialCustomerSearch.toLowerCase();
                      return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
                        (u.mobile || '').includes(specialCustomerSearch);
                    })
                    .slice(0, 10)
                    .map(u => (
                      <div
                        key={u.id}
                        onClick={() => { setSelectedSpecialCustomer(u); setSpecialCustomerSearch(`${u.firstName} ${u.lastName}`); }}
                        style={{
                          padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                          backgroundColor: selectedSpecialCustomer?.id === u.id ? '#e8f5e9' : 'transparent',
                        }}
                      >
                        <Avatar src={u.profile_image || undefined} style={{ width: 30, height: 30, fontSize: 13 }}>
                          {u.firstName?.[0]}
                        </Avatar>
                        <div>
                          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '13px' }}>{u.firstName} {u.lastName}</Typography>
                          {u.mobile ? <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '11px', color: '#888' }}>{u.mobile}</Typography> : null}
                        </div>
                      </div>
                    ))
                  }
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <MuiButton
                    variant="contained"
                    disabled={!selectedSpecialCustomer}
                    onClick={handlePublishSpecialCustomer}
                    style={{ backgroundColor: MAIN_COLOR, color: '#fff', fontFamily: FONT_FAMILY }}
                  >
                    Publish
                  </MuiButton>
                  {sawiSpecialData?.data?.customer?.active && (
                    <MuiButton variant="outlined" onClick={handleHideSpecialCustomer} style={{ fontFamily: FONT_FAMILY }}>
                      Hide from App
                    </MuiButton>
                  )}
                </div>
              </Grid>

              {/* Special Driver Picker */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', marginBottom: '8px' }}>
                  🌟 Sawi Special Driver
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by name or phone"
                  value={specialDriverSearch}
                  onChange={e => { setSpecialDriverSearch(e.target.value); setSelectedSpecialDriver(null); }}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '10px' }}>
                  {(usersdata || [])
                    .filter(u => {
                      if (u.usertype !== 'driver' || !specialDriverSearch.length) return false;
                      const q = specialDriverSearch.toLowerCase();
                      return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
                        (u.mobile || '').includes(specialDriverSearch);
                    })
                    .slice(0, 10)
                    .map(u => (
                      <div
                        key={u.id}
                        onClick={() => { setSelectedSpecialDriver(u); setSpecialDriverSearch(`${u.firstName} ${u.lastName}`); }}
                        style={{
                          padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                          backgroundColor: selectedSpecialDriver?.id === u.id ? '#e8f5e9' : 'transparent',
                        }}
                      >
                        <Avatar src={u.profile_image || undefined} style={{ width: 30, height: 30, fontSize: 13 }}>
                          {u.firstName?.[0]}
                        </Avatar>
                        <div>
                          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '13px' }}>{u.firstName} {u.lastName}</Typography>
                          {u.mobile ? <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '11px', color: '#888' }}>{u.mobile}</Typography> : null}
                        </div>
                      </div>
                    ))
                  }
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <MuiButton
                    variant="contained"
                    disabled={!selectedSpecialDriver}
                    onClick={handlePublishSpecialDriver}
                    style={{ backgroundColor: MAIN_COLOR, color: '#fff', fontFamily: FONT_FAMILY }}
                  >
                    Publish
                  </MuiButton>
                  {sawiSpecialData?.data?.driver?.active && (
                    <MuiButton variant="outlined" onClick={handleHideSpecialDriver} style={{ fontFamily: FONT_FAMILY }}>
                      Hide from App
                    </MuiButton>
                  )}
                </div>
              </Grid>
            </Grid>
          )}
        </Paper>
      </div>
  );
}
